<template>
  <b-container fluid class="p-4 bg-white rounded shadow-sm">
    <b-row>
      <b-col cols="12">
        <h5 class="mb-3 text-primary font-semibold">Job Library Detail</h5>
      </b-col>

      <!-- Two-column detail: left labels, right values -->
      <b-col cols="12">
        <div class="table-responsive">
          <table class="table table-striped mb-0">
            <tbody>
              <tr>
                <td class="text-muted w-25">Job Code</td>
                <td class="font-semibold">{{ selectedData?.code || '-' }}</td>
              </tr>
              <tr>
                <td class="text-muted">Job Name</td>
                <td class="font-semibold">{{ selectedData?.name || '-' }}</td>
              </tr>
              <tr>
                <td class="text-muted">Category</td>
                <td class="font-semibold text-uppercase">
                  {{
                    selectedData?.maintenance_component?.component_name || '-'
                  }}
                </td>
              </tr>
              <tr>
                <td class="text-muted">Equipment</td>
                <td class="font-semibold">
                  {{ selectedData?.maintenance_master?.equipment_name || '-' }}
                </td>
              </tr>
              <tr>
                <td class="text-muted">Job Type</td>
                <td class="font-semibold">
                  {{
                    selectedData?.maintenance_job_type?.job_type_index || '-'
                  }}
                </td>
              </tr>
              <tr>
                <td class="text-muted">Job Class</td>
                <td class="font-semibold">
                  {{ selectedData?.maintenance_job_class?.job_class || '-' }}
                </td>
              </tr>
              <tr>
                <td class="text-muted">Method</td>
                <td class="font-semibold">{{ selectedData?.method || '-' }}</td>
              </tr>
              <tr v-if="selectedData?.method == 'CALENDAR'">
                <td class="text-muted">Interval</td>
                <td class="font-semibold">
                  {{ formatInterval(selectedData) }}
                </td>
              </tr>
              <tr>
                <td class="text-muted align-top">Job Description</td>
                <td class="font-semibold">
                  <div
                    v-if="selectedData?.job_description"
                    v-html="selectedData.job_description"
                    class="job-description-content"
                  ></div>
                  <span v-else>-</span>
                </td>
              </tr>
              <tr>
                <td class="text-muted">Active</td>
                <td class="font-semibold">
                  <b-badge
                    :variant="selectedData?.active ? 'success' : 'secondary'"
                  >
                    {{ selectedData?.active ? 'Active' : 'Inactive' }}
                  </b-badge>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-col>

      <!-- Array data section: Counter Triggers -->
      <b-col cols="12" v-if="counterRows.length" class="mt-4">
        <h5 class="mb-2 font-semibold">Counter Triggers</h5>
        <div class="table-responsive">
          <table class="table table-bordered align-middle">
            <thead class="thead-light">
              <tr>
                <th style="width: 40%">Engine Name</th>
                <th style="width: 30%">Engine Type</th>
                <th style="width: 30%" class="text-right">Interval (Hours)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(ctr, i) in counterRows" :key="i">
                <td>{{ ctr.engine_label }}</td>
                <td>{{ ctr?.engine_type }}</td>
                <td class="text-right">
                  {{ formatNumber(ctr.interval_hour) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import moment from '@/node_modules/moment/moment'

export default {
  name: 'DetailJobLibraries',
  props: {
    selectedData: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    counterRows() {
      const jlCounters = this.selectedData?.job_library_counters || []
      const ctrTriggers = this.selectedData?.counter_triggers || []

      // Normalize both shapes to { engine_label, interval_hour }
      const rowsA = jlCounters.map((c) => ({
        engine_label: c?.power_series
          ? `${c.power_series?.name || 'Power Series'}${
              c.power_series?.model ? ' - ' + c.power_series?.model : ''
            }`
          : `ID: ${c?.power_series_id ?? '-'}`,
        engine_type:c?.power_type?.name || '-',
        interval_hour: c?.interval_hour ?? c?.interval ?? 0,
      }))
      const rowsB = ctrTriggers.map((c) => ({
        engine_label: c?.power_series?.name
          ? `${c.power_series?.name}${
              c.power_series?.model ? ' - ' + c.power_series?.model : ''
            }`
          : `ID: ${c?.power_series_id ?? '-'}`,
        interval_hour: c?.interval_hour ?? c?.interval ?? 0,
      }))
      return [...rowsA, ...rowsB]
    },
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '-'
      return moment(dateStr).format('DD MMM YYYY HH:mm')
    },
    formatNumber(val) {
      if (val === null || val === undefined) return '-'
      return new Intl.NumberFormat('id-ID').format(val)
    },
    formatInterval(job) {
      if (!job) return '-'
      if (job?.method === 'CALENDAR') {
        const interval = Number(job?.calendar_interval) || 0
        const unit = (job?.calendar_type || '').toLowerCase()
        if (!interval || !unit) return '-'
        const label = unit.charAt(0).toUpperCase() + unit.slice(1)
        return `${interval} ${label}${interval > 1 ? 's' : ''}`
      }
      if (job?.method === 'COUNTER') {
        const total = this.counterRows.reduce(
          (acc, r) => acc + (Number(r.interval_hour) || 0),
          0
        )
        return total
          ? `${this.formatNumber(total)} Hours (total across engines)`
          : '-'
      }
      return '-'
    },
  },
}
</script>

<style scoped>
.table th,
.table td {
  vertical-align: middle;
}
.white-space-preline {
  white-space: pre-line;
}
.font-semibold {
  font-weight: 500;
}
.job-description-content {
  line-height: 1.5;
}
</style>

<style>
.job-description-content ul,
.job-description-content ol {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
  list-style-type: disc;
}
.job-description-content li {
  margin: 0.25rem 0;
  display: list-item;
}
.job-description-content p {
  margin: 0.5rem 0;
}
.job-description-content p:first-child {
  margin-top: 0;
}
.job-description-content p:last-child {
  margin-bottom: 0;
}
</style>
